import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Plus, CalendarDays, List, BarChart3, Gift, Users, UserCheck, History } from 'lucide-react';
import { User } from 'firebase/auth';
import { onAuthStateChange } from './services/auth';
import { fetchUserStats } from './services/tasks';
import { userService } from './services/userService';
import { firestoreService } from './services/firestore';
import GoogleSignIn from './components/GoogleSignIn';
import TaskList from './components/TaskList';
import TaskForm from './components/TaskForm';
import Calendar from './components/Calendar';
import Rewards from './components/Rewards';
import UserManagement from './components/UserManagement';
import ApproverRequests from './components/ApproverRequests';
import RewardHistory from './components/RewardHistory';
import { UserStats } from './types/task';
import { NotificationProvider } from './contexts/NotificationContext';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  min-height: 100vh;
`;

const Header = styled.header`
  padding: 32px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }
`;

const Logo = styled.h1`
  font-size: 28px;
  font-weight: 900;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: -0.5px;
  transition: transform 0.2s;
  flex-shrink: 0;
  
  &:hover {
    transform: scale(1.01);
  }
  
  @media (max-width: 768px) {
    font-size: 24px;
    gap: 8px;
  }
`;

const MainContent = styled.main`
  padding: 0 0 48px 0;
`;

const WelcomeSection = styled(motion.section)`
  text-align: center;
  padding: 80px 40px;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  border-radius: 24px;
  margin-bottom: 64px;
  box-shadow: 0 20px 40px rgba(245, 124, 0, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 56px;
  font-weight: 900;
  margin-bottom: 24px;
  letter-spacing: -1px;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

const WelcomeSubtitle = styled.p`
  font-size: 22px;
  opacity: 0.95;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const Dashboard = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
`;

const StatsCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 1px solid #f1f5f9;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 900;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 32px;
  background: white;
  border-radius: 12px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
`;

const TabButton = styled(motion.button)<{ active: boolean }>`
  background: ${(props: { active: boolean }) => props.active ? '#F57C00' : 'transparent'};
  color: ${(props: { active: boolean }) => props.active ? 'white' : '#64748b'};
  border: none;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: center;

  &:hover {
    background: ${(props: { active: boolean }) => props.active ? '#E65100' : '#f8fafc'};
    color: ${(props: { active: boolean }) => props.active ? 'white' : '#1e293b'};
  }
`;

const AddTaskButton = styled(motion.button)`
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(245, 124, 0, 0.4);
  }
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  flex-direction: column;
`;

const LoginHeader = styled.header`
  padding: 24px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 32px;
  padding-right: 32px;
  border-bottom: 1px solid rgba(245, 124, 0, 0.1);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
  gap: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    padding: 20px 32px;
  }
`;

const LoginHero = styled.section`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 900;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 24px;
  letter-spacing: -2px;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 24px;
  color: #64748b;
  margin-bottom: 48px;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 32px;
  margin-bottom: 64px;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 32px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
  border: 1px solid #f1f5f9;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  font-size: 28px;
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  color: #64748b;
  line-height: 1.5;
`;

const SignInSection = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const SignInPrompt = styled.p`
  font-size: 20px;
  color: #374151;
  margin-bottom: 32px;
  font-weight: 600;
`;

const HeroSignInSection = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const CenteredSignInSection = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #374151;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
`;

type TabType = 'tasks' | 'calendar' | 'stats' | 'rewards' | 'requests' | 'history';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('tasks');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showApproverRequests, setShowApproverRequests] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [shouldShowRequestsTab, setShouldShowRequestsTab] = useState(false);
  const [taskUpdateTrigger, setTaskUpdateTrigger] = useState(0);

  const [showUserManagement, setShowUserManagement] = useState(false);


  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  const loadUserStats = useCallback(async () => {
    if (!user) return;
    
    try {
      const stats = await fetchUserStats();
      setUserStats(stats);
    } catch (error) {
      console.error('Error loading user stats:', error);
      // Fallback to default stats if API fails
      const defaultStats: UserStats = {
        totalTasks: 0,
        completedTasks: 0,
        completionRate: 0,
        streakDays: 0,
        averageTasksPerDay: 0,
        earnedCredits: 0,
        availableCredits: 0
      };
      setUserStats(defaultStats);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadUserStats();
    }
  }, [user, taskUpdateTrigger, loadUserStats]);

  const handleTaskCreated = () => {
    setShowTaskForm(false);
    setTaskUpdateTrigger(prev => prev + 1);
  };

  const handleTaskUpdate = () => {
    setTaskUpdateTrigger(prev => prev + 1);
  };

  const handleTaskScheduled = () => {
    setSelectedTask(null);
    setTaskUpdateTrigger(prev => prev + 1);
  };

  const handlePurchaseReward = (rewardId: string, cost: number) => {
    // Update user stats to reflect the purchase
    if (userStats) {
      setUserStats(prev => prev ? {
        ...prev,
        availableCredits: prev.availableCredits - cost
      } : null);
    }
    console.log(`Purchased reward ${rewardId} for ${cost} credits`);
  };

  // Check if user should see the Requests tab
  const checkRequestsTabVisibility = useCallback(async () => {
    if (!user) {
      setShouldShowRequestsTab(false);
      return;
    }

    try {
      // Check if user is a credit approver (has accepted approver relationships)
      const pendingMissions = await firestoreService.getPendingMissionsForApprovers();
      
      // Check if user has pending approver requests
      const pendingApproverRequests = await userService.getPendingApproverRequests();
      
      // Check if user is an accepted credit approver for anyone
      const usersIApprove = await userService.getUsersIApprove();
      
      // Check if user has pending mission verifications
      const pendingVerifications = await firestoreService.getPendingMissionVerifications();
      
      // Show Requests tab if user is an approver or has pending requests
      setShouldShowRequestsTab(
        pendingMissions.length > 0 || 
        pendingApproverRequests.length > 0 || 
        usersIApprove.length > 0 ||
        pendingVerifications.length > 0
      );
    } catch (error) {
      console.error('Error checking requests tab visibility:', error);
      setShouldShowRequestsTab(false);
    }
  }, [user]);

  useEffect(() => {
    checkRequestsTabVisibility();
  }, [checkRequestsTabVisibility]);


  if (!user) {
    return (
      <LoginContainer>
        <LoginHeader>
          <Logo>
            <Rocket />
            Mission
          </Logo>
          <GoogleSignIn
            user={user}
            onSignIn={setUser}
            onSignOut={() => setUser(null)}
          />
        </LoginHeader>

        <LoginHero>
          <HeroContent>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <HeroTitle>Transform Tasks Into Missions</HeroTitle>
              <HeroSubtitle>
                Build discipline, achieve goals, and track your progress with our intelligent 
                task management system. Turn everyday tasks into rewarding missions.
              </HeroSubtitle>
            </motion.div>

            <FeaturesGrid>
              <FeatureCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <FeatureIcon>
                  <Rocket />
                </FeatureIcon>
                <FeatureTitle>Mission Management</FeatureTitle>
                <FeatureDescription>
                  Create and organize tasks with priority levels, deadlines, and progress tracking.
                </FeatureDescription>
              </FeatureCard>

              <FeatureCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <FeatureIcon>
                  <CalendarDays />
                </FeatureIcon>
                <FeatureTitle>Smart Scheduling</FeatureTitle>
                <FeatureDescription>
                  Schedule tasks to available time slots and optimize your daily routine.
                </FeatureDescription>
              </FeatureCard>

              <FeatureCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <FeatureIcon>
                  <Gift />
                </FeatureIcon>
                <FeatureTitle>Rewards System</FeatureTitle>
                <FeatureDescription>
                  Earn credits for completing tasks and redeem them for exciting rewards.
                </FeatureDescription>
              </FeatureCard>

              <FeatureCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <FeatureIcon>
                  <BarChart3 />
                </FeatureIcon>
                <FeatureTitle>Progress Analytics</FeatureTitle>
                <FeatureDescription>
                  Track your completion rates, streaks, and overall productivity metrics.
                </FeatureDescription>
              </FeatureCard>
            </FeaturesGrid>
          </HeroContent>
        </LoginHero>
      </LoginContainer>
    );
  }

  return (
    <NotificationProvider>
      <Container>
        <Header>
          <Logo>
            <Rocket />
            Mission
          </Logo>
          <GoogleSignIn
            user={user}
            onSignIn={setUser}
            onSignOut={() => setUser(null)}
            onUserManagement={() => setShowUserManagement(true)}
          />
        </Header>

        <MainContent>
        {userStats && (
          <Dashboard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <StatNumber>{userStats.totalTasks}</StatNumber>
              <StatLabel>Total Missions</StatLabel>
            </StatsCard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <StatNumber>{userStats.completionRate}%</StatNumber>
              <StatLabel>Completion Rate</StatLabel>
            </StatsCard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <StatNumber>{userStats.streakDays}</StatNumber>
              <StatLabel>Day Streak</StatLabel>
            </StatsCard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <StatNumber>{userStats.averageTasksPerDay}</StatNumber>
              <StatLabel>Avg Tasks/Day</StatLabel>
            </StatsCard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <StatNumber>{userStats.earnedCredits}</StatNumber>
              <StatLabel>Earned Credits</StatLabel>
            </StatsCard>
            <StatsCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <StatNumber>{userStats.availableCredits}</StatNumber>
              <StatLabel>Available Credits</StatLabel>
            </StatsCard>
          </Dashboard>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <TabContainer>
            <TabButton
              active={activeTab === 'tasks'}
              onClick={() => setActiveTab('tasks')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <List />
              Missions
            </TabButton>
            <TabButton
              active={activeTab === 'calendar'}
              onClick={() => setActiveTab('calendar')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <CalendarDays />
              Schedule
            </TabButton>
            <TabButton
              active={activeTab === 'stats'}
              onClick={() => setActiveTab('stats')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <BarChart3 />
              Progress
            </TabButton>
            <TabButton
              active={activeTab === 'rewards'}
              onClick={() => setActiveTab('rewards')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Gift />
              Rewards
            </TabButton>
            <TabButton
              active={activeTab === 'history'}
              onClick={() => setActiveTab('history')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <History />
              Wallet
            </TabButton>
            {shouldShowRequestsTab && (
              <TabButton
                active={activeTab === 'requests'}
                onClick={() => setActiveTab('requests')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserCheck />
                Requests
              </TabButton>
            )}
          </TabContainer>

        </div>

        {activeTab === 'tasks' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              padding: '0 8px'
            } as React.CSSProperties}
          >
            <h2 style={{ 
              margin: 0, 
              fontSize: '28px', 
              fontWeight: '800',
              background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.5px'
            } as React.CSSProperties}>
              Your Missions
            </h2>
            <AddTaskButton
              onClick={() => setShowTaskForm(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(245, 124, 0, 0.3)',
                transition: 'all 0.2s'
              } as React.CSSProperties}
            >
              <Plus size={16} />
              New Mission
            </AddTaskButton>
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {activeTab === 'tasks' && (
            <motion.div
              key="tasks"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {showTaskForm ? (
                <TaskForm
                  onTaskCreated={handleTaskCreated}
                  onCancel={() => setShowTaskForm(false)}
                />
              ) : (
                <TaskList
                  onTaskUpdate={handleTaskUpdate}
                />
              )}
            </motion.div>
          )}

          {activeTab === 'calendar' && (
            <motion.div
              key="calendar"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Calendar
                userId={user.uid}
                selectedTask={selectedTask}
                onTaskScheduled={handleTaskScheduled}
              />
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <EmptyState
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <EmptyStateIcon>
                  <BarChart3 />
                </EmptyStateIcon>
                <EmptyStateTitle>Progress Tracking</EmptyStateTitle>
                <EmptyStateText>
                  Detailed analytics and progress tracking features coming soon!
                </EmptyStateText>
              </EmptyState>
            </motion.div>
          )}

          {activeTab === 'rewards' && (
            <motion.div
              key="rewards"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Rewards
                userId={user.uid}
                availableCredits={userStats?.availableCredits || 0}
                onPurchaseReward={handlePurchaseReward}
              />
            </motion.div>
          )}

          {activeTab === 'requests' && (
            <motion.div
              key="requests"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ApproverRequests
                onClose={() => setActiveTab('tasks')}
              />
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <RewardHistory
                onClose={() => setActiveTab('tasks')}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {showUserManagement && (
          <motion.div
            key="user-management"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            } as React.CSSProperties}
          >
            <UserManagement
              onClose={() => setShowUserManagement(false)}
            />
          </motion.div>
        )}
      </MainContent>
    </Container>
    </NotificationProvider>
  );
}

export default App; 